/**
 * Global tab keys (docs/ux/project-tab-spec.md §1.1, §1.11; navigation-ia.md §3.7): ⌥1–9 in a
 * browser tab, ⌘/Ctrl+1–9 in the installed app, and `g 1`…`g 9` everywhere; 0 is Home (⌥0, ⌘0,
 * `g 0`). Mounted once in the app shell while the tab shell is on.
 */
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useRef } from 'react'
import { projectHref } from '../../lib/ia.js'
import { useProjects, useRuns } from '../../lib/queries.js'
import { homeTabKey, isStandalone, isTyping, tabIndexFromKey } from '../../lib/shortcut.js'
import { placeOf, tabOfPath } from '../../lib/tab-memory.js'
import { bootstrapOrder, lastActivity, needsBootstrap, pinnedTabs } from '../../lib/tab-order.js'

export function useTabShortcuts(enabled: boolean) {
  const nav = useNavigate()
  const projects = useProjects()
  const runs = useRuns()
  const ids = useMemo(() => {
    const list = projects.data ?? []
    const fallback = needsBootstrap(list)
      ? bootstrapOrder(list, lastActivity(runs.data ?? []))
      : undefined
    return pinnedTabs(list, fallback).map((p) => p.id)
  }, [projects.data, runs.data])
  const latest = useRef(ids)
  latest.current = ids
  useEffect(() => {
    if (!enabled) return
    const standalone = isStandalone()
    let g = 0
    const go = (i: number) => {
      const id = latest.current[i]
      if (!id) return false
      // The tab reopens where you were (its view, inspector and run).
      void nav({ href: placeOf(id, projectHref(id), tabOfPath(location.pathname)) })
      return true
    }
    const home = () => void nav({ href: placeOf('home', '/home', tabOfPath(location.pathname)) })
    const onKey = (e: KeyboardEvent) => {
      // A dialog owns ⌥-digits (the Capture sheet's mode and target keys).
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      if (homeTabKey(e, standalone)) {
        e.preventDefault()
        home()
        return
      }
      const i = tabIndexFromKey(e, standalone)
      if (i != null) {
        if (go(i)) e.preventDefault()
        return
      }
      if (isTyping(e) || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'g') {
        g = Date.now()
        return
      }
      if (g && Date.now() - g < 1200 && /^[0-9]$/.test(e.key)) {
        g = 0
        if (e.key === '0') {
          e.preventDefault()
          home()
        } else if (go(Number(e.key) - 1)) e.preventDefault()
      }
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [enabled, nav])
}
